<template>
  <patient-profile>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
    <div class="patient-basic__anamnesis">
      <div class="patient-basic__anamnesis__form">
        <textarea-length-limitation
          placeholder="Жалобы"
          rows="4"
          title="Жалобы"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="complaintsInputError"
          v-model="anamnesis.complaints"
          name="complaints"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <textarea-length-limitation
          placeholder="Аллергические реакции"
          rows="4"
          title="Аллергические реакции"
          name="allergic-reactions"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="allergicReactionsInputError"
          v-model="anamnesis.allergicReactions"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary"
        />
        <textarea-length-limitation
          placeholder="Перенесённые и сопутствующие заболевания"
          rows="4"
          title="Перенесённые и сопутствующие заболевания"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="pastDiseasesInputError"
          v-model="anamnesis.pastDiseases"
          name="past-diseases"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <textarea-length-limitation
          placeholder="Принимаемые лекарственные препараты"
          rows="4"
          title="Принимаемые лекарственные препараты"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="medicationsTakenInputError"
          v-model="anamnesis.medicationsTaken"
          name="medications-taken"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <textarea-length-limitation
          placeholder="Развитие настоящего заболевания"
          rows="4"
          title="Развитие настоящего заболевания"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="actualDiseaseProgressionInputError"
          v-model="anamnesis.actualDiseaseProgression"
          name="actual-disease-progression"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <textarea-length-limitation
          placeholder="Данные объективного исследования, внешний осмотр"
          rows="4"
          title="Данные объективного исследования, внешний осмотр"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="externalExaminationInputError"
          v-model="anamnesis.externalExamination"
          name="external-examination"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <textarea-length-limitation
          placeholder="Осмотр преддверия и полости рта"
          rows="4"
          title="Осмотр преддверия и полости рта"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="examinationOralCavityInputError"
          v-model="anamnesis.examinationOralCavity"
          name="examination-oral-cavity"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <div class="patient-basic__anamnesis__oral">
          <div class="patient-basic__anamnesis__oral__title">
            <div class="patient-basic__caption">Зубная формула</div>
            <div class="patient-basic__anamnesis__oral__switch print-hide">
              <p>Молочный прикус</p>
              <div>
                <v-switch
                  @click.stop
                  v-model="anamnesis.isMilkBite"
                  inset
                  hide-details />
              </div>
            </div>
          </div>
          <div class="patient-basic__anamnesis__oral__container">
            <oral-examination
              v-bind:diagnoses="$options.diagnosisList"
              v-bind:teeth="teethDentalCavities"
              v-on:tooth-change="onToothChange"
            />
            <div class="patient-basic__anamnesis__oral__legend">
              <div class="patient-basic__caption">Условные обозначения:</div>
              <div class="patient-basic__anamnesis__oral__legend__items">
                <div
                  class="patient-basic__anamnesis__oral__legend__item"
                  v-for="{ name, description } in $options.diagnosisLegends"
                  v-bind:key="name">
                  <div class="patient-basic__anamnesis__oral__legend__item__name">
                    {{ name }}
                  </div>
                  -
                  <div
                    class="
                    patient-basic__anamnesis__oral__legend__item__description
                  ">
                    {{ description }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <textarea-length-limitation
          placeholder="Прикус"
          rows="4"
          title="Прикус"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="biteInputError"
          v-model="anamnesis.bite"
          name="bite"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <div class="patient-basic__anamnesis__row">
          <textarea-length-limitation
            placeholder="Индекс гигиены"
            rows="2"
            title="Индекс гигиены"
            v-bind:maxLength="200"
            v-bind:show-counter="false"
            v-bind:violations="hygieneIndexInputError"
            v-model="anamnesis.hygieneIndex"
            name="hygiene-index"
            v-on:focus="onAnamnesisFieldFocus($event.target.name)"
            v-on:blur="onBlurAnamnesisLibrary" />
          <textarea-length-limitation
            placeholder="Индекс КПУ"
            rows="2"
            title="Индекс КПУ"
            v-bind:maxLength="200"
            v-bind:show-counter="false"
            v-bind:violations="cpuIndexInputError"
            v-model="anamnesis.cpuIndex"
            name="complaints" />
        </div>
        <textarea-length-limitation
          placeholder="Лучевая нагрузка"
          rows="4"
          title="Лучевая нагрузка"
          v-bind:maxLength="$options.maxTextLength"
          v-bind:show-counter="false"
          v-bind:violations="radiationDoseSummaryInputError"
          v-model="anamnesis.radiationDoseSummary"
          name="radiation-dose-summary"
          v-on:focus="onAnamnesisFieldFocus($event.target.name)"
          v-on:blur="onBlurAnamnesisLibrary" />
        <button
          class="patient-basic__button-save"
          type="button"
          v-bind:disabled="isSaveDisabled"
          v-on:click="onSave">
          Сохранить
        </button>
      </div>
      <anamnesis-phrase-library
        ref="anamnesisPhraseLibraryRef"
        v-bind:phrases="selectedAnamnesisLibraryPhrases"
        v-bind:show="hasFocusedAnamnesisField"
        v-on:blur="onBlurAnamnesisLibrary"
        v-on:input-phrase="onInputAnamnesisPhrase"
        v-on:show-phrase-dialog="showCreatePhraseDialog"
        v-on:remove-phrase="onRemoveAnamnesisPhrase"
        v-on:edit-phrase="showCreatePhraseDialog"
      />
    </div>
    <modal-dialog
      v-bind:title="inputDialogAnamnesisPhraseTitle"
      v-model="shownCreatePhraseDialog"
      with-cross
    >
      <form
        ref="form"
        v-on:submit.prevent="onSavePhrase"
      >
        <input-text-length-limitation
          v-bind:title="inputDialogAnamnesisPhraseTitle"
          v-bind:max-length="200"
          v-model="editedPhrase"
        />
        <button
          type="submit"
          class="patient-profile__modal__button"
        >{{ titleSaveButton }}</button>
      </form>
    </modal-dialog>
  </patient-profile>
</template>

<script>
import OralExamination from '@/components/OralExamination.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import {
  Anamnesis,
  DentalCavities,
  EditPatientAnamnesisRequest, EditPatientDentalCavitiesRequest,
} from '@/storage/gateways/PatientGateway';
import debounce from 'lodash.debounce';
import { List, Maybe, Validation } from 'monet';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import notifierMixin from '@/mixins/notifierMixin';
import PatientProfile from '@/views/ClinicPanel/PatientProfile.vue';
import CollapseField from '@/components/CollapseField.vue';
import InputTextSearch from '@/components/InputTextSearch.vue';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import InputTextLengthLimitation from '@/components/InputTextLengthLimitation.vue';
import ModalDialog from '@/components/ModalDialog.vue';

/**
 * @description Время для debounce
 * @type {number}
 */
const WAIT_TIME = 300;

/**
 * @description Группы крови.
 * @type {{id:number,name:string}[]}
 */
const bloodTypes = [
  { id: 1, name: 'Первая (I)' },
  { id: 2, name: 'Вторая (II)' },
  { id: 3, name: 'Третья (III)' },
  { id: 4, name: 'Четвертая (IV)' },
];

/**
 * @description Типы инвалидности.
 * @type {{id:string,name:string}[]}
 */
const disabilityTypes = [
  { id: 'Отсутствует', name: 'Отсутствует' },
  { id: 'Первая (I)', name: 'Первая (I)' },
  { id: 'Вторая (II)', name: 'Вторая (II)' },
  { id: 'Третья (III)', name: 'Третья (III)' },
];

/**
 * @description Резус фактор.
 * @type {{id:number,name:string}[]}
 */
const rpFactors = [
  { id: 0, name: 'Отрицательный (-)' },
  { id: 1, name: 'Положительный (+)' },
];

/**
 * @description Условные обозначения.
 * @type {{name:string, description:string}[]}
 */
const diagnosisLegends = [
  { name: '0', description: 'отсутствует' },
  { name: 'П', description: 'пломбированный' },
  { name: 'R', description: 'корень' },
  { name: 'А', description: 'пародонтоз' },
  { name: 'C', description: 'кариес' },
  { name: 'К', description: 'коронка' },
  { name: 'P', description: 'пульпит' },
  { name: 'I,II,III', description: 'степень подвижности' },
  { name: 'Pt', description: 'периодонтит' },
  { name: 'И', description: 'искусственный зуб' },
  { name: 'Кл', description: 'клиновидный дефект' },
  { name: 'ПС', description: 'пломба/кариес' },
];

/**
 * @description Список диагнозов.
 * @type {{name:string, description:string}[]}
 */
const diagnosisList = [
  { name: '', description: 'выберите значение' },
  { name: '0', description: '0 - отсутствует' },
  { name: 'П', description: 'П - пломбированный' },
  { name: 'R', description: 'R - корень' },
  { name: 'А', description: 'А - пародонтоз' },
  { name: 'C', description: 'C - кариес' },
  { name: 'К', description: 'К - коронка' },
  { name: 'P', description: 'P - пульпит' },
  { name: 'I', description: 'I степень подвижности' },
  { name: 'II', description: 'II степень подвижности' },
  { name: 'III', description: 'III степень подвижности' },
  { name: 'Pt', description: 'Pt - периодонтит' },
  { name: 'И', description: 'И - искусственный зуб' },
  { name: 'Кл', description: 'Кл - клиновидный дефект' },
  { name: 'ПС', description: 'ПС - пломба/кариес' },
];

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Библиотека фраз анамнеза.
 * @type {Vue}
 */
const AnamnesisPhraseLibrary = {
  components: {
    CollapseField,
    InputTextSearch,
  },
  computed: {
    /**
     * @description Отфильтрованные фразы.
     * @type {string[]}
     */
    filteredPhrases() {
      if (this.phraseQuery !== '') {
        const query = this.phraseQuery.toLowerCase();
        const filterAndFlat = (items) => items.flatMap((item) => {
          if (item.withChildren) {
            return [...filterAndFlat(item.value)];
          }
          return item.value.toLowerCase().includes(query) ? [item] : [];
        });
        return filterAndFlat(this.phrases);
      }
      const expandAndFlat = (items) => items.flatMap((item) => {
        if (item.withChildren) {
          const isExpanded = this.expandedFolderKeys.includes(item.key);
          if (isExpanded) {
            return [
              { ...item, class: ['expanded'] },
              ...expandAndFlat(item.value),
            ];
          }
        }
        return [{ ...item, class: [] }];
      });
      return expandAndFlat(this.phrases);
    },
  },
  data() {
    return {
      /**
       * @description Ключи открытых папок.
       * @type {string[]}
       */
      expandedFolderKeys: [],
      /**
       * @description Запрос на поиск по фразам.
       * @type {string}
       */
      phraseQuery: '',
      /**
       * @description Выбранные фразы.
       * @type {string[]}
       */
      selectedPhrases: [],
    };
  },
  methods: {
    onInputPhrase(key, value) {
      this.selectedPhrases.push(key);
      this.$listeners['input-phrase'](value);
    },
    onShowPhraseDialog() {
      this.$listeners['show-phrase-dialog']();
    },
    onRemovePhrase(phraseId) {
      this.$listeners['remove-phrase'](phraseId);
    },
    onEditPhrase(phraseId, phrase) {
      this.$listeners['edit-phrase'](phraseId, phrase);
    },
    /**
     * @description Рекурсивно раскрыть все папки в библиотеке фраз.
     * @param {array} libraryPhrases
     * @returns {void}
     */
    expandAllFolders(libraryPhrases) {
      const collectFolderKeys = (items) => items.flatMap((item) => {
        if (item?.withChildren) {
          return [item.key, ...collectFolderKeys(item.value)];
        }
        return [];
      });
      this.expandedFolderKeys = collectFolderKeys(libraryPhrases);
    },
    toggleFolder(item) {
      const filteredKeys = this.expandedFolderKeys.filter((key) => key !== item.key);
      const isNotExpanded = filteredKeys.length === this.expandedFolderKeys.length;
      if (item.type === 'folder') {
        this.expandedFolderKeys = isNotExpanded ? [item.key] : filteredKeys;
      } else {
        if (isNotExpanded) {
          filteredKeys[1] = item.key;
        }
        this.expandedFolderKeys = filteredKeys;
      }
    },
  },
  props: {
    /**
     * @description Фразы.
     * @type {string[]}
     */
    phrases: {
      required: true,
      type: Array,
    },
    /**
     * @description Показать.
     * @type {boolean}
     */
    show: {
      required: true,
      type: Boolean,
    },
  },
  render(createElement) {
    const libraryData = {
      attrs: {
        tabindex: -1,
        ...(this.show ? {} : { style: 'display: none;' }),
      },
      class: 'anamnesis-phrase-library',
      on: { focusout: this.$listeners.blur },
    };
    const onClickPhrase = ({ key, value }) => () => this.onInputPhrase(key, value);
    const onClickDropdown = ({ key, value }) => (event) => {
      this.onInputPhrase(
        key,
        value[event.target.selectedIndex - 1].value,
      );
    };
    const toggleFolder = (item) => () => this.toggleFolder(item);
    const onShowPhraseDialog = () => () => this.onShowPhraseDialog();
    const onRemovePhrase = (id) => () => this.onRemovePhrase(id);
    const onEditPhrase = (id, value) => () => this.onEditPhrase(id, value);
    const createFolderOrPhraseElement = (item) => {
      if (item.withChildren) {
        return createElement(
          'button',
          {
            attrs: {
              class: [
                item.type === 'folder'
                  ? 'anamnesis-phrase-library__folder' : 'anamnesis-phrase-library__subfolder',
                ...item.class,
              ].join(' '),
              title: 'Переключить видимость фраз в папке',
              type: 'button',
            },
            key: item.key,
            on: { click: toggleFolder(item) },
          },
          item.title,
        );
      }
      const addPhraseButton = !Array.isArray(item.value) || item.value.length < 2 ? createElement(
        'button',
        {
          attrs: {
            title: 'Вставить фразу в поле протокола',
            type: 'button',
          },
          class: { selected: this.selectedPhrases.includes(item.key) },
          on: { click: onClickPhrase(item) },
          staticClass: 'anamnesis-phrase-library__phrase',
          style: `margin-left: ${(item.key.match(/subfolder/g) || []).length * 20}px`,
        },
        item.title,
      )
        : createElement(
          'div',
          {
            staticClass: 'anamnesis-phrase-library__dropdown',
            style: `margin-left: ${(item.key.match(/subfolder/g) || []).length * 20}px`,
          },
          [
            createElement(
              'select',
              {
                attrs: {
                  title: 'Вставить фразу в поле протокола',
                  value: item.title,
                },
                class: { selected: this.selectedPhrases.includes(item.key) },
                staticClass: 'anamnesis-phrase-library__phrase',
                on: { input: onClickDropdown(item) },
              },
              [
                createElement(
                  'option',
                  { values: item.title, attrs: { hidden: true, selected: true } },
                  item.title,
                ),
                ...item.value.map((value) => createElement(
                  'option',
                  { attrs: { value: value.value, title: value.value } },
                  value.title,
                )),
              ],
            ),
          ],
        );
      if (item.id) {
        const removePhraseButton = createElement(
          'button',
          {
            attrs: {
              class: 'anamnesis-phrase-library__button-delete',
              title: 'Удалить фразу',
              type: 'button',
            },
            on: { click: onRemovePhrase(item.id) },
          },
        );
        const editPhraseButton = createElement(
          'button',
          {
            attrs: {
              class: 'anamnesis-phrase-library__button-edit',
              title: 'Редактировать фразу',
              type: 'button',
            },
            on: { click: onEditPhrase(item.id, item.value) },
          },
        );
        return createElement(
          'div',
          {
            attrs: {
              class: 'anamnesis-phrase-library__buttons',
            },
          },
          [
            addPhraseButton,
            editPhraseButton,
            removePhraseButton,
          ],
        );
      }
      return addPhraseButton;
    };
    const addButtonElement = createElement(
      'button',
      {
        attrs: {
          class: 'anamnesis-phrase-library__button-add',
          title: 'Добавить новую фразу в библиотеку',
          type: 'button',
        },
        on: { click: onShowPhraseDialog() },
      },
      'Добавить шаблон',
    );
    if (this.phrases.length !== 0) {
      const filterInputElement = createElement(InputTextSearch, {
        on: { input: (updatedValue) => { this.phraseQuery = updatedValue; } },
        props: { value: this.phraseQuery },
      });
      return createElement('fieldset', libraryData, [
        filterInputElement,
        ...this.filteredPhrases.map(createFolderOrPhraseElement),
        addButtonElement,
      ]);
    }
    const hasNotPhrasesElement = createElement('em', 'Для поля нет подходящих фраз.');
    return createElement('fieldset', libraryData, [hasNotPhrasesElement, addButtonElement]);
  },
};

/**
 * @description Примесь библиотеки фраз анамнеза.
 * @type {Vue}
 */
const AnamnesisLibraryMixin = {
  computed: {
    /**
     * @description Свойство поля анамнеза по ключу поля в фокусе.
     * @type {Maybe<string>}
     */
    focusedAnamnesisFieldProperty() {
      return this
        .focusedAnamnesisField
        .bind((key) => Maybe.fromEmpty(this.$options.resolveAnamnesisFieldProperty(key)));
    },
    /**
     * @description Имеет поле анамнеза в фокусе.
     * @type {boolean}
     */
    hasFocusedAnamnesisField() {
      return this.focusedAnamnesisField.isSome();
    },
    /**
     * @description Выбранная библиотека анамнеза.
     * @type {Maybe<object>}
     */
    selectedAnamnesisLibrary() {
      const findPhrasesByField = (libraries) => (field) => libraries
        .find((library) => library.field === field);
      return this
        .focusedAnamnesisField
        .bind(findPhrasesByField(this.anamnesisPhraseLibraries));
    },
    /**
     * @description Фразы выбранной библиотеки анамнеза.
     * @type {string[]}
     */
    selectedAnamnesisLibraryPhrases() {
      return this.selectedAnamnesisLibrary.map(({ phrases }) => phrases).orSome([]);
    },
    /**
     * @description Текст кнопки сохранения шаблона фразы
     * @returns {string}
     */
    titleSaveButton() {
      return this.editingIdPhrase ? 'Сохранить' : 'Добавить';
    },
    /**
     * @description Скрыть выбранную библиотеку фраз
     * @returns {boolean}
     */
    hideSelectedAnamnesisLibrary() {
      return !this.shownCreatePhraseDialog && !this.showDeletePhraseDialog;
    },
  },
  data() {
    return {
      /**
       * @description Библиотеки фраз анамнеза.
       * @type {List<{field: string, phrases: string[]}>}
       */
      anamnesisPhraseLibraries: List(),
      /**
       * @description Идентификатор редактируемой фразы
       * @type {string|null}
       */
      editingIdPhrase: null,
      /**
       * @description Фраза
       * @type {string}
       */
      editedPhrase: '',
      /**
       * @description Сфокусированное поле анамнеза.
       * @type {Maybe<string>}
       */
      focusedAnamnesisField: Maybe.None(),
      /**
       * @description Заголовок поля ввода фразы
       * @type {string}
       */
      inputDialogAnamnesisPhraseTitle: 'Введите слово или фразу, используя знаки препинания',
      /**
       * @description Показывать диалог создания/редактирования фраз
       */
      shownCreatePhraseDialog: false,
      /**
       * @description Отображается окно удаления фразы
       */
      showDeletePhraseDialog: false,
      /**
       * @description Отображается категория "Мои шаблоны"
       */
      showMyTemplates: false,
      /**
       * @description Флаг для отслеживания раскрытия категорий
       * @type {bool}
       */
      isAlreadyExpanded: false,
    };
  },
  methods: {
    /**
     * @description Получить библиотеку фраз для поля анамнеза.
     * @param {string} field Поле.
     * @returns {Promise}
     */
    fetchAnamnesisLibrary(field) {
      return this
        .$patientGateway
        .viewAnamnesisPhraseLibrary(field)
        .then((result) => result.map((data) => {
          const assignPhraseFieldProps = (prefix) => (items) => items.map((item, index) => {
            const { title, type } = item;
            const key = `${prefix}:${index}-${type}`;
            const withChildren = type === 'folder' || type === 'subfolder';
            const value = withChildren ? assignPhraseFieldProps(key)(item.value) : item.value;
            const id = item.id ?? null;
            return {
              key,
              title,
              value,
              withChildren,
              id,
              type,
            };
          });
          return {
            field,
            phrases: assignPhraseFieldProps(field)(data.phrases),
          };
        }))
        .then((library) => {
          this.anamnesisPhraseLibraries = library.toList();
          if (this.showMyTemplates) {
            this.unfoldMyTemplates();
            this.showMyTemplates = false;
          }
        });
    },
    /**
     * @description Показать каталог "Мои шаблоны".
     * @returns void
     */
    unfoldMyTemplates() {
      const myTemplatesFolder = this.selectedAnamnesisLibraryPhrases.find((item) => item.title === 'Мои шаблоны');
      if (
        !this.$refs.anamnesisPhraseLibraryRef.expandedFolderKeys.includes(myTemplatesFolder?.key)
      ) {
        this.$refs.anamnesisPhraseLibraryRef.toggleFolder(myTemplatesFolder);
      }
    },
    /**
     * @description Обработать снятие фокуса с библиотеки фраз.
     * @param {any} event Событие.
     * @returns {void}
     */
    onBlurAnamnesisLibrary(event) {
      const isOutLibraryFocus = event.relatedTarget === null
        || event.relatedTarget.closest('.anamnesis-phrase-library') === null;
      if (isOutLibraryFocus && this.hideSelectedAnamnesisLibrary) {
        this.focusedAnamnesisField = Maybe.None();
        this.isAlreadyExpanded = false;
      }
    },
    /**
     * @description Обработать фокус поля анамнеза.
     * @param {string} field Название поля.
     * @returns {void}
     */
    onAnamnesisFieldFocus(field) {
      this.focusedAnamnesisField = Maybe.of(field);
      this.fetchAnamnesisLibrary(field).then(() => {
        if (!this.alreadyExpanded) {
          const phraseLibraryRef = this.$refs.anamnesisPhraseLibraryRef;
          if (phraseLibraryRef && this.selectedAnamnesisLibraryPhrases.length > 0) {
            phraseLibraryRef.expandAllFolders(this.selectedAnamnesisLibraryPhrases);
            this.isAlreadyExpanded = true;
          }
        }
      });
    },
    /**
     * @description Обработать ввод фразы из библиотеки.
     * @param {string} phrase Фраза.
     * @returns {void}
     */
    onInputAnamnesisPhrase(phrase) {
      if (this.focusedAnamnesisField.val) {
        const property = this.focusedAnamnesisFieldProperty.val;
        this.anamnesis.appendPhraseIntoField(property, phrase);
      }
    },
    /**
     * @description Обработать сохранение новой/редактируемой фразы
     * @returns {void}
     */
    onSavePhrase() {
      if (this.editingIdPhrase) {
        this.onEditAnamnesisPhrase(this.editingIdPhrase, this.editedPhrase);
      } else {
        this.onCreateAnamnesisPhrase(this.focusedAnamnesisField, this.editedPhrase);
        this.showMyTemplates = true;
      }
    },
    /**
     * @description Создать шаблон фразы протокола лечения
     * @param {string} field  Поле привязки фразы
     * @param {string} phrase Фраза
     * @returns {void}
     */
    onCreateAnamnesisPhrase(field, phrase) {
      this
        .$patientGateway
        .createAnamnesisPhrase(field.val, phrase)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.shownCreatePhraseDialog = false;
            this.onAnamnesisFieldFocus(this.focusedAnamnesisField.val);
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    /**
     * @description Удалить шаблон фразы протокола лечения
     * @param {string} phraseId Идентификатор фразы
     * @returns {void}
     */
    async onRemoveAnamnesisPhrase(phraseId) {
      this.showDeletePhraseDialog = true;
      const dialog = await this.showRemoveDialog('Вы действительно хотите удалить шаблон?');
      if (dialog.result !== DialogResult.DELETE) {
        this.showDeletePhraseDialog = false;
        return;
      }
      this
        .$patientGateway
        .removeAnamnesisPhrase(phraseId)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.onAnamnesisFieldFocus(this.focusedAnamnesisField.val);
            this.$nextTick(() => {
              this.$refs.anamnesisPhraseLibraryRef.$el.focus();
              this.showDeletePhraseDialog = false;
            });
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
            this.showDeletePhraseDialog = false;
          });
        });
    },
    /**
     * @description Изменить шаблон фразы протокола лечения
     * @param {string} phraseId Идентификатор фразы
     * @param {string} phrase   Фраза
     * @returns {void}
     */
    onEditAnamnesisPhrase(phraseId, phrase) {
      this
        .$patientGateway
        .editAnamnesisPhrase(phraseId, phrase)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.shownCreatePhraseDialog = false;
            this.onAnamnesisFieldFocus(this.focusedAnamnesisField.val);
          });
          result.forEachLeft(({ title }) => { this.notifyFail(title); });
        });
    },
    /**
     * @description Показ диалога создания новой фразы
     * @param {string|null} phraseId Идентификатор фразы
     * @param {string}      phrase   Фраза
     * @returns {void}
     */
    showCreatePhraseDialog(phraseId = null, phrase = '') {
      this.shownCreatePhraseDialog = true;
      this.editingIdPhrase = phraseId;
      this.editedPhrase = phrase;
    },
  },
  /**
   * @description Получить ключ поля анамнеза по сфокусированному полю.
   * @param {string} name Название поля.
   * @returns {(string|null)}
   */
  resolveAnamnesisFieldProperty: (name) => {
    const fields = {
      allergicReactions: 'allergic-reactions',
      diagnosis: 'diagnosis',
      complaints: 'complaints',
      pastDiseases: 'past-diseases',
      externalExamination: 'external-examination',
      bite: 'bite',
      radiationDoseSummary: 'radiation-dose-summary',
      medicationsTaken: 'medications-taken',
      actualDiseaseProgression: 'actual-disease-progression',
      examinationOralCavity: 'examination-oral-cavity',
      hygieneIndex: 'hygiene-index',
    };
    return Object.keys(fields).find((key) => fields[key] === name) || null;
  },
};

export default {
  bloodTypes,
  components: {
    ModalDialog,
    InputTextLengthLimitation,
    PatientProfile,
    ModalDialogPreventLeave,
    OralExamination,
    TextareaLengthLimitation,
    AnamnesisPhraseLibrary,
  },
  computed: {
    allergicReactionsInputError: resolveInputErrors('anamnesis.allergic-reactions'),
    biteInputError: resolveInputErrors('anamnesis.bite'),
    complaintsInputError: resolveInputErrors('anamnesis.complaints'),
    externalExaminationInputError: resolveInputErrors('anamnesis.external-examination'),
    medicationsTakenInputError: resolveInputErrors('anamnesis.medications-taken'),
    actualDiseaseProgressionInputError: resolveInputErrors('anamnesis.actual-disease-progression'),
    examinationOralCavityInputError: resolveInputErrors('anamnesis.examination-oral-cavity'),
    hygieneIndexInputError: resolveInputErrors('anamnesis.hygiene-index'),
    cpuIndexInputError: resolveInputErrors('anamnesis.cpu-index'),
    labDataInputError: resolveInputErrors('anamnesis.lab-data'),
    oralMucosaInputError: resolveInputErrors('anamnesis.oral-mucosa'),
    pastDiseasesInputError: resolveInputErrors('anamnesis.past-diseases'),
    /**
     * @description Предотвращать уход со страницы.
     * @type {boolean}
     */
    needPreventLeave() {
      return this.isDentalCavitiesChanged || this.isAnamnesisChanged;
    },
    /**
     * @description Сохранение отключено
     * @param {boolean} isAwaitRequest
     * @param {boolean} needPreventLeave
     * @return {boolean}
     */
    isSaveDisabled: ({ isAwaitRequest, needPreventLeave }) => !needPreventLeave || isAwaitRequest,
    /**
     * @description Идентификатор пациента
     * @param vm контекст
     * @return {number}
     */
    patientId: (vm) => parseInt(vm.$route.params.id, 10),
    presentDiseasesInputError: resolveInputErrors('anamnesis.present-diseases'),
    radiationDoseSummaryInputError: resolveInputErrors('anamnesis.radiation-dose-summary'),
    rhFactorInputError: resolveInputErrors('anamnesis.rh-factor'),
    /**
     * @description Зубы полости рта.
     * @type {any[]}
     */
    teethDentalCavities({ dentalCavities }) {
      if (!dentalCavities) {
        return [];
      }
      return this.anamnesis.isMilkBite ? dentalCavities.teeth.slice(32, dentalCavities.teeth.length)
        : dentalCavities.teeth.slice(0, 32);
    },
  },
  created() {
    this.fetchPatientAnamnesis();
  },
  data() {
    return {
      /**
       * @description Анамнез.
       * @type {Anamnesis}
       */
      anamnesis: new Anamnesis(),
      /**
       * @description Полость рта.
       * @type {DentalCavities}
       */
      dentalCavities: new DentalCavities(),
      /**
       * @description Нарушение ограничений.
       * @type {any}
       */
      inputErrors: {},
      /**
       * @description Анамнез изменен.
       * @type {boolean}
       */
      isAnamnesisChanged: false,
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitRequest: false,
      /**
       * @description Полость рта изменена.
       * @type {boolean}
       */
      isDentalCavitiesChanged: false,
    };
  },
  diagnosisLegends,
  diagnosisList,
  disabilityTypes,
  methods: {
    /**
     * @description Затянуть анамнез с полость рта.
     * @return {void}
     */
    fetchPatientAnamnesis() {
      const onSuccess = ({ anamnesis, dentalCavities, errors }) => {
        this.anamnesis = anamnesis;
        this.dentalCavities = dentalCavities;
        const failurePairs = errors.causes.map((cause) => [cause.source, cause.details]);
        this.inputErrors = Object.fromEntries(failurePairs) || {};
      };
      this
        .$patientGateway
        .getAnamnesis(this.patientId)
        .then((result) => result.fold(this.notifyFail, onSuccess));
    },
    /**
     * @description Сохранить изменения
     */
    onSave() {
      const requests = [];
      if (this.isDentalCavitiesChanged) {
        requests.push(this.sendDentalCavitiesEditRequest());
      }
      if (this.isAnamnesisChanged) {
        requests.push(this.sendAnamnesisEditRequest());
      }
      this.isAwaitRequest = true;
      return Promise
        .all(requests)
        .then((values) => {
          this.inputErrors = {};
          let isSuccess = true;
          values.forEach((result) => {
            /** response {causes: [], title: String} */
            result.forEachFail((response) => {
              isSuccess = false;
              if (!response.causes) {
                return;
              }
              const failurePairs = response.causes.map((cause) => [cause.source, cause.details]);
              if (failurePairs.length !== 0) {
                this.notifyFail(response.title);
              }
              Object.assign(this.inputErrors, Object.fromEntries(failurePairs) || {});
            });
          });
          this.isAwaitRequest = false;
          if (isSuccess) {
            this.notifySuccess('Данные успешно обновлены');
            this.fetchPatientAnamnesis();
            return Validation.Success('Данные успешно обновлены');
          }
          return Validation.Fail();
        });
    },
    /**
     * @description Обработать изменение зуба.
     */
    onToothChange(toothNumber, { part, value }) {
      const tooth = this.dentalCavities.teeth.find((item) => item.number === toothNumber);
      tooth[part] = value;
      // this.dentalCavities.teeth[i][part] = value;
    },
    /**
     * @description сохранить и уйти
     * @return {Promise<void>}
     */
    async saveAndLeave() {
      const result = await this.onSave();
      result.forEach((message) => {
        this.notifySuccess(message);
        this.actionAfterClosingDialogLeaveConfirm();
      });
      this.isShownDialogLeaveConfirm = false;
    },
    /**
     * @description Отправить запрос на изменение анамнеза.
     * @returns {Promise<Validation>}
     */
    async sendAnamnesisEditRequest() {
      const request = new EditPatientAnamnesisRequest(this.anamnesis, this.patientId);
      const result = await this.$patientGateway.editAnamnesis(request);
      result.forEach(() => {
        this.anamnesis.refresh();
        this.isAnamnesisChanged = false;
      });
      return result;
    },
    /**
     * @description Отправить запрос на изменение ротовой полости.
     * @returns {Promise<Validation>}
     */
    async sendDentalCavitiesEditRequest() {
      const request = new EditPatientDentalCavitiesRequest(this.dentalCavities, this.patientId);
      const result = await this.$patientGateway.editDentalCavities(request);
      result.forEach(() => {
        this.dentalCavities.refresh();
        this.isDentalCavitiesChanged = false;
      });
      return result;
    },
  },
  mixins: [
    preventLeaveWithoutConfirmMixin,
    notifierMixin,
    AnamnesisLibraryMixin,
    builtInDialogMixin,
  ],
  name: 'ClinicPanelPatientAnamnesis',
  rpFactors,
  watch: {
    anamnesis: {
      handler: debounce(function update(value) {
        this.isAnamnesisChanged = value.isChanged();
      }, WAIT_TIME),
      deep: true,
    },
    dentalCavities: {
      handler: debounce(function update(value) {
        this.isDentalCavitiesChanged = value.isChanged();
      }, WAIT_TIME),
      deep: true,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.anamnesis-phrase-library {
  position: sticky;
  top: 50px;
  background-color: var(--primary-background-color);
  border: none;
  min-height: 300px;
  overflow: scroll;
  max-height: 80vh;
  height: fit-content;
  counter-reset: counter;
  @media screen and (max-width: 600px) {
    grid-area: library;
    max-height: inherit;
    min-height: inherit;
    top: inherit;
    position: relative;
  }
  .v-input {
    position: sticky;
    top: -0.35rem;
    background: white;
    margin: 0 0 39px 0;
    z-index: 1;
  }
  &__folder {
    display: flex;
    padding: 5px 14px;
    font-weight: 500;
    color: #323C47;
    font-size: 18px;
    font-style: normal;
    width: 100%;
    &:first-child {
      margin-top: 50px;
    }
    &:before {
      counter-increment: counter;
      content: counter(counter) ". ";
      margin: 0 20px 0 0;
    }
    &:after {
      content: '';
      background-image: url("@/assets/img/checked/arrow-top-blue.svg");
      background-repeat: no-repeat;
      margin: 0 0 0 auto;
      width: 20px;
      height: 20px;

    }
    &.expanded {
      color: #0489E0;
    }
    &.expanded::after {
      transform: rotate(-180deg);
    }
  }
  &__subfolder {
    display: flex;
    text-align: left;
    padding: 5px 24px;
    font-weight: 500;
    color: #323C47;
    font-size: 18px;
    font-style: normal;
    width: 100%;
    &:first-child {
      margin-top: 50px;
      margin-left: 20px;
    }
    & > * {
      margin-left: 20px;
    }
    &:after {
      content: '';
      background-image: url("@/assets/img/checked/arrow-top-blue.svg");
      background-repeat: no-repeat;
      margin: 0 0 0 auto;
      width: 20px;
      height: 20px;

    }
    &.expanded {
      color: #0489E0;
    }
    &.expanded::after {
      transform: rotate(-180deg);
    }
  }
  &__phrase {
    display: flex;
    align-items: center;
    text-align: left;
    word-break: break-word;
    padding: 7px 14px;
    font-size: 14px;
    border-radius: 3px;
    background: #EAEFF3;
    color: #323C47;
    transition: all 0.3s ease 0s;
    cursor: pointer;
    margin-top: 5px;
    &:hover {
      background-color: #0076C4;
      color: #FFF;
      transition: all 0.3s ease 0s;
    }
    &.selected {
      background-color: #0076C4;
      color: #FFF;
    }
  }
  &__dropdown {
    display: flex;
    align-items: center;
    width: 100%;
    select {
      border: 1px solid #c2cfe0;
      outline: none;
      background-color: transparent;
      width: 100%;
      &:hover {
        background-color: transparent;
        color: #192a3e;
      }
    }
    &:after {
      position: relative;
      top: 7px;
      right: 25px;
      content: '';
      background-image: url("@/assets/img/checked/arrow-top-blue.svg");
      background-repeat: no-repeat;
      width: 20px;
      height: 20px;
    }
    &:focus {
      color: #192a3e;
      background-color: #fff;
      border: 1px solid #0489e0;
      outline: 0;
    }
  }
  &__buttons {
    display: grid;
    grid-template-columns: auto 12px 15px;
    align-items: center;
    grid-gap: 10px;
  }
  &__button {
    &-add {
      @include buttons.btn-transparent(100%, 48px, 16px);
      margin: 20px 0 0 0;
      max-width: 174px;
    }
    &-delete {
      display: inline-flex;
      background-size: contain;
      background-repeat: no-repeat;
      width: 12px;
      height: 12px;
      cursor: pointer;
      background-image: url("@/assets/img/exit-grey.svg");
      &:hover {
        background-image: url("@/assets/img/exit-blue.svg");
      }
    }
    &-edit {
      display: inline-flex;
      background-image: url("@/assets/img/pen-grey.svg");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      width: 15px;
      min-width: 15px;
      height: 15px;
      min-height: 15px;
      padding: 0;
      cursor: pointer;
      &:hover {
        background-image: url("@/assets/img/pen-blue.svg");
      }
    }
  }
}

.patient {
  &-basic {
    &__caption {
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #192a3e;
    }
    &__button-save {
      @include buttons.btn-blue(100%, 48px, 16px);
      max-width: 174px;
      padding: 0;
      margin: 10px 0 0 0;
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
    &__anamnesis {
      display: grid;
      grid-template: "form" "library" / 1fr 1fr;
      gap: 10px;
      width: 100%;
      margin: 37px 0 0 0;
      @media screen and (max-width: 1000px) {
        grid-template: "library"
                   "form" ;
        gap: 0;
      }
      &__form {
        display: flex;
        flex-direction: column;
        gap: 20px;
        width: 100%;
        overflow: hidden;
      }
      &__row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
        margin: 0 0 20px 0;
      }
      &__library {
        margin-top: 20px;
        display: grid;
        grid-row: 5;
        grid-template-columns: repeat(2, minmax(130px, auto));
        align-items: center;
        gap: 5px 22px;
      }
      &__oral {
        margin: 0 0 10px 0;
        overflow: scroll;
        &__title {
          flex-direction: row;
          display: flex;
          align-items: center;
          gap: 32px;
        }
        &__switch {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 20px;
          p {
            margin: 0;
            font-weight: 400;
            font-size: 14px;
            line-height: 17px;
            color: var(--reading-color);
          }
          .v-input--selection-controls__input:hover,
          .v-input--selection-controls__ripple:before {
            content: none;
          }
          .v-input--selection-controls__ripple {
            display: none;
          }
          .v-input--switch .v-input--switch__track {
            background: white !important;
            border: 1px solid #c2cfe0;
            border-radius: 15px;
            opacity: 1 !important;
          }
          .v-input--switch .v-input--switch__thumb {
            color: #90a0b7;
          }
          .v-input--selection-controls {
            margin: 0 !important;
            padding: 5px !important;
          }
        }
        &__container {
          display: flex;
          flex-direction: column;
          gap: 32px;
          margin: 18px 0 0 0;
          width: min-content;
          .oral-examination {
            margin-left:10px;
          }
        }
        &__legend {
          min-width: 370px;
          &__items {
            margin-top: 20px;
            display: grid;
            grid-row: 5;
            grid-template-columns: repeat(2, minmax(130px, auto));
            align-items: center;
            gap: 5px 22px;
          }
          &__item {
            height: 24px;
            font-weight: 400;
            font-size: 14px;
            line-height: 17px;
            flex-direction: row;
            display: flex;
            color: #192a3e;
            align-items: center;
            gap: 4px;
            &__name {
              min-width: 20px;
              font-weight: 600;
              font-size: 16px;
              line-height: 19px;
            }
            &__description {
              font-weight: 400;
              font-size: 14px;
              line-height: 17px;
              color: #707683;
            }
          }
        }
      }
    }
  }
  &-profile__modal__button {
    @include buttons.btn-blue(100%, 48px, 16px);
    max-width: 174px;
    &:disabled {
      background-color: var(--unfocus-color);
    }
  }
}

@media screen and (max-width: 1400px) {
  .patient-basic {
    &__anamnesis {
      &__oral {
        &__container {
          flex-direction: column;
        }
      }
    }
  }
}

@media screen and (max-width: 970px) {
  .patient-basic {
    &__anamnesis {
      &__row {
        grid-template-columns: 1fr;
      }
      &__oral {
        overflow: scroll;
        &__container {
          width: 100%;
        }
      }

    }
  }
}
</style>
